import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, resolveImage, formatPrice, formatError } from "@/lib/api";

const STATUSES = ["Nuovo", "Pagato", "Da preparare", "Spedito", "Completato", "Annullato"];
const PAYMENT_LABELS = { paid: "Pagato", pending: "In attesa", failed: "Fallito", expired: "Scaduto", refunded: "Rimborsato" };

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setO(data);
  };
  useEffect(() => { load(); }, [id]);

  const changeStatus = async (v) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status: v });
      toast.success("Stato aggiornato");
      load();
    } catch (e) {
      toast.error("Errore", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  if (!o) return <AdminLayout title="Ordine" back="/admin/ordini"><p className="text-slate-500">Caricamento...</p></AdminLayout>;

  return (
    <AdminLayout title={o.order_number} back="/admin/ordini">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: items + totals */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Prodotti</h2>
            <div className="space-y-3">
              {o.line_items.map((li, i) => (
                <div key={i} data-testid={`order-item-${i}`} className="flex gap-3 items-center border-b border-border last:border-0 pb-3 last:pb-0">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {li.image && <img src={resolveImage(li.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{li.title}</div>
                    <div className="text-xs text-slate-500">Qta {li.quantity} × {formatPrice(li.unit_price)}</div>
                  </div>
                  <div className="text-sm font-heading font-black text-brand-blue">{formatPrice(li.subtotal)}</div>
                </div>
              ))}
              {o.digital_items?.map((d) => (
                <div key={d.pdf_id} data-testid={`order-pdf-${d.pdf_id}`} className="flex justify-between text-sm border-b border-border last:border-0 pb-2">
                  <span className="truncate">PDF digitale: {d.title}</span>
                  <span className={d.kind === "gift" ? "text-emerald-600 font-bold" : "font-bold text-brand-blue"}>
                    {d.kind === "gift" ? "OMAGGIO" : formatPrice(d.unit_price)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotale</span><span>{formatPrice(o.subtotal)}</span></div>
              {o.pdf_subtotal > 0 && <div className="flex justify-between"><span>PDF digitali</span><span>{formatPrice(o.pdf_subtotal)}</span></div>}
              <div className="flex justify-between"><span>Spedizione</span><span>{o.shipping === 0 ? "GRATUITA" : formatPrice(o.shipping)}</span></div>
              <div className="flex justify-between font-heading font-black text-brand-blue text-lg pt-2 border-t border-border mt-1">
                <span>Totale</span><span data-testid="order-total">{formatPrice(o.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: status + customer */}
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Stato</h2>
            <label className="text-xs font-bold text-slate-500 uppercase">Stato ordine</label>
            <Select value={o.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="order-status-select" className="mt-1 rounded-xl h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-3">
              <label className="text-xs font-bold text-slate-500 uppercase">Pagamento</label>
              <div className="mt-1">
                <Badge className={`${o.payment_status === "paid" ? "bg-emerald-600" : "bg-slate-500"} text-white text-xs uppercase font-bold`}>
                  {PAYMENT_LABELS[o.payment_status] || o.payment_status}
                </Badge>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Data: {new Date(o.created_at).toLocaleString("it-IT")}
            </div>
            {o.stripe_session_id && (
              <div className="mt-1 text-[10px] text-slate-400 font-mono truncate" title={o.stripe_session_id}>
                Ref: {o.stripe_session_id.slice(0, 24)}...
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Cliente</h2>
            <div className="text-sm space-y-0.5">
              <div className="font-bold">{o.customer.first_name} {o.customer.last_name}</div>
              <div className="text-slate-600">{o.customer.email}</div>
              {o.customer.phone && <div className="text-slate-600">{o.customer.phone}</div>}
            </div>
            <div className="mt-3 pt-3 border-t border-border text-sm">
              <div className="text-xs uppercase font-bold text-slate-500 mb-1">Indirizzo di spedizione</div>
              <div className="text-slate-700">
                {o.customer.address_line}, {o.customer.house_number}<br />
                {o.customer.postal_code} {o.customer.city} ({o.customer.province})<br />
                {o.customer.country}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
